import { CommandHandlerMap } from '@commandDrivenStateEngine/core/types';
import { AccountState, emptyArcaPointsOfSale, emptyBillingEntityFormData, emptyGroupFormData, emptyUserFormData, selectors } from './state';
import { AccountCommand } from './commands';

/**
 * Pure handler functions for account commands.
 *
 * Rules:
 * - MUST be pure (no side effects, no API calls)
 * - MUST return new state object (immutable)
 * - MUST NOT dispatch other commands
 * - Keep handlers simple and focused
 */

// =============================================================================
// TAB HANDLERS
// =============================================================================

const handleTabSet = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/tab/set' }>
): AccountState => {
  return {
    ...state,
    activeTab: cmd.payload.tab
  };
};

// =============================================================================
// ACCOUNT DATA HANDLERS
// =============================================================================

const handleDataSetAccount = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/data/set' }>
): AccountState => {
  return {
    ...state,
    account: cmd.payload.account
  };
};

const handleDataClearAccount = (
  state: AccountState,
  _cmd: Extract<AccountCommand, { type: 'account/data/clear' }>
): AccountState => {
  return {
    ...state,
    account: null
  };
};

// =============================================================================
// ACCOUNT INFO HANDLERS
// =============================================================================

const handleInfoStartEdit = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/info/startEdit' }>
): AccountState => {
  // Get current value for the field being edited
  let currentValue = '';
  if ( cmd.payload.field === 'businessName' ) {
    currentValue = state.account?.accountName || '';
  }

  return {
    ...state,
    accountInfo: {
      ...state.accountInfo,
      editingField: cmd.payload.field,
      tempValue: currentValue
    }
  };
};

const handleInfoUpdateTemp = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/info/updateTemp' }>
): AccountState => {
  return {
    ...state,
    accountInfo: {
      ...state.accountInfo,
      tempValue: cmd.payload.value
    }
  };
};

const handleInfoSave = (
  state: AccountState,
  _cmd: Extract<AccountCommand, { type: 'account/info/save' }>
): AccountState => {
  // Mark as saving (policy will handle backend call)
  return {
    ...state,
    accountInfo: {
      ...state.accountInfo,
      loading: {
        ...state.accountInfo.loading,
        saving: true
      }
    }
  };
};

const handleInfoCancel = (
  state: AccountState,
  _cmd: Extract<AccountCommand, { type: 'account/info/cancel' }>
): AccountState => {
  return {
    ...state,
    accountInfo: {
      ...state.accountInfo,
      editingField: null,
      tempValue: ''
    }
  };
};

const handleInfoSetSaving = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/info/loading/setSaving' }>
): AccountState => {
  return {
    ...state,
    accountInfo: {
      ...state.accountInfo,
      loading: {
        ...state.accountInfo.loading,
        saving: cmd.payload.isSaving
      },
      // Clear editing state if saving is complete
      ...( !cmd.payload.isSaving ? { editingField: null, tempValue: '' } : {})
    }
  };
};

// =============================================================================
// BILLING DATA HANDLERS
// =============================================================================

const handleBillingDataUpsert = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/billing/data/upsert' }>
): AccountState => {
  return {
    ...state,
    billing: {
      ...state.billing,
      entities: {
        ...state.billing.entities,
        [cmd.payload.entity.id!]: cmd.payload.entity
      }
    }
  };
};

const handleBillingDataRemove = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/billing/data/remove' }>
): AccountState => {
  const { [cmd.payload.entityId]: removed, ...rest } = state.billing.entities;
  return {
    ...state,
    billing: {
      ...state.billing,
      entities: rest
    }
  };
};

const handleBillingDataSetAll = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/billing/data/setAll' }>
): AccountState => {
  return {
    ...state,
    billing: {
      ...state.billing,
      entities: cmd.payload.entities
    }
  };
};

const handleBillingDataSetInvoicingIntegrations = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/billing/data/setInvoicingIntegrations' }>
): AccountState => {
  return {
    ...state,
    billing: {
      ...state.billing,
      invoicingIntegrations: cmd.payload.integrations
    }
  };
};

// =============================================================================
// BILLING LIST HANDLERS
// =============================================================================

const handleBillingListSetSearch = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/billing/list/setSearch' }>
): AccountState => {
  return {
    ...state,
    billing: {
      ...state.billing,
      searchQuery: cmd.payload.query,
      currentPage: 1 // Reset to page 1 when search changes
    }
  };
};

const handleBillingListSetPage = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/billing/list/setPage' }>
): AccountState => {
  return {
    ...state,
    billing: {
      ...state.billing,
      currentPage: cmd.payload.page
    }
  };
};

// =============================================================================
// BILLING FORM HANDLERS
// =============================================================================

const handleBillingFormOpenAdd = (
  state: AccountState,
  _cmd: Extract<AccountCommand, { type: 'account/billing/form/openAdd' }>
): AccountState => {
  return {
    ...state,
    billing: {
      ...state.billing,
      formMode: { type: 'add' },
      formData: { ...emptyBillingEntityFormData },
      formErrors: {},
      arcaPointsOfSale: emptyArcaPointsOfSale
    }
  };
};

const handleBillingFormOpenEdit = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/billing/form/openEdit' }>
): AccountState => {
  const entity = selectors.getBillingEntityById( state, cmd.payload.entityId );
  if ( !entity ) {
    console.warn( 'Billing entity not found for edit:', cmd.payload.entityId );
    return state;
  }

  return {
    ...state,
    billing: {
      ...state.billing,
      formMode: { type: 'edit', entityId: cmd.payload.entityId },
      formData: {
        name: entity.name || '',
        legalName: entity.legalName || '',
        documentId: entity.documentId || '',
        billingAddress: entity.billingAddress || '',
        vatCondition: entity.vatCondition as any,
        grossIncome: entity.grossIncome || '',
        activityStartDate: entity.activityStartDate || '',
        // TV1-2210 (S6) — carry the WHOLE ARCA record into the form. S4 mapped it
        // down to `String( p.number )` here and back up at submit, which SILENTLY
        // DROPPED `emisionTipo` / `bloqueado` / `fchBaja` on every edit round trip.
        // Harmless while nothing populated them; the S6 picker does, and the user
        // cannot re-author what the form never showed them.
        pointsOfSale: [...( entity.pointsOfSale || [])],
        taxAgency: entity.taxAgency || 'arca',
        invoicingIntegrationId: entity.invoicingIntegrationId || ''
      },
      formErrors: {},
      arcaPointsOfSale: emptyArcaPointsOfSale
    }
  };
};

const handleBillingFormClose = (
  state: AccountState,
  _cmd: Extract<AccountCommand, { type: 'account/billing/form/close' }>
): AccountState => {
  return {
    ...state,
    billing: {
      ...state.billing,
      formMode: { type: 'closed' },
      formData: { ...emptyBillingEntityFormData },
      formErrors: {},
      arcaPointsOfSale: emptyArcaPointsOfSale
    }
  };
};

const handleBillingFormUpdateField = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/billing/form/updateField' }>
): AccountState => {
  const newFormData = {
    ...state.billing.formData,
    [cmd.payload.field]: cmd.payload.value
  };

  // Clear error for this field
  const newErrors = { ...state.billing.formErrors };
  delete newErrors[cmd.payload.field];

  return {
    ...state,
    billing: {
      ...state.billing,
      formData: newFormData,
      formErrors: newErrors
    }
  };
};

const handleBillingFormSetErrors = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/billing/form/setErrors' }>
): AccountState => {
  return {
    ...state,
    billing: {
      ...state.billing,
      formErrors: cmd.payload.errors
    }
  };
};

/**
 * TV1-2210 (S6) — set the whole selection from the picker.
 *
 * The picker yields ARCA point-of-sale NUMBERS; the form holds STRUCTURED
 * records. Each number is resolved against the fetched ARCA list FIRST (the
 * authority for `emisionTipo` / `bloqueado` / `fchBaja`), then against what the
 * form already holds — which is what preserves those three opaque fields on an
 * edit of an entity whose list has not been (or cannot be) fetched. A number
 * that resolves to neither is kept as a bare `{ number }` rather than dropped:
 * losing a user's selection silently is worse than carrying it without the
 * ARCA metadata it never had.
 */
const handleBillingFormSetPointsOfSale = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/billing/form/setPointsOfSale' }>
): AccountState => {
  const fromArca = new Map(
    state.billing.arcaPointsOfSale.items.map(( p ) => [ Number( p.number ), p ])
  );
  const fromForm = new Map(
    state.billing.formData.pointsOfSale.map(( p ) => [ Number( p.number ), p ])
  );

  const pointsOfSale = cmd.payload.numbers.map(
    ( n ) => fromArca.get( n ) ?? fromForm.get( n ) ?? { number: n }
  );

  const newErrors = { ...state.billing.formErrors };
  delete newErrors.pointsOfSale;

  return {
    ...state,
    billing: {
      ...state.billing,
      formData: { ...state.billing.formData, pointsOfSale },
      formErrors: newErrors
    }
  };
};

const handleBillingFormRemovePointOfSale = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/billing/form/removePointOfSale' }>
): AccountState => {
  return {
    ...state,
    billing: {
      ...state.billing,
      formData: {
        ...state.billing.formData,
        pointsOfSale: state.billing.formData.pointsOfSale.filter(
          ( p ) => Number( p.number ) !== cmd.payload.point
        )
      }
    }
  };
};

/**
 * TV1-2210 (S6) — the ARCA points-of-sale fetch, as its three settled states.
 *
 * Each carries the `integrationId` it belongs to and the reducer stores it, so
 * a late response for a previously-selected integration can never be offered
 * against the one now on the form.
 */
const handleArcaPointsOfSaleLoading = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/billing/arcaPointsOfSale/loading' }>
): AccountState => ({
  ...state,
  billing: {
    ...state.billing,
    arcaPointsOfSale: {
      integrationId: cmd.payload.integrationId,
      items: [],
      loading: true,
      loaded: false
    }
  }
});

const handleArcaPointsOfSaleLoaded = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/billing/arcaPointsOfSale/loaded' }>
): AccountState => ({
  ...state,
  billing: {
    ...state.billing,
    arcaPointsOfSale: {
      integrationId: cmd.payload.integrationId,
      items: cmd.payload.items,
      loading: false,
      loaded: true
    }
  }
});

const handleArcaPointsOfSaleFailed = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/billing/arcaPointsOfSale/failed' }>
): AccountState => ({
  ...state,
  billing: {
    ...state.billing,
    arcaPointsOfSale: {
      integrationId: cmd.payload.integrationId,
      items: [],
      loading: false,
      loaded: true,
      error: cmd.payload.error
    }
  }
});

const handleBillingFormSubmit = (
  state: AccountState,
  _cmd: Extract<AccountCommand, { type: 'account/billing/form/submit' }>
): AccountState => {
  // Mark as submitting (policy will handle backend call)
  return {
    ...state,
    billing: {
      ...state.billing,
      loading: {
        ...state.billing.loading,
        submitting: true
      }
    }
  };
};

// =============================================================================
// BILLING LOADING HANDLERS
// =============================================================================

const handleBillingLoadingSetEntities = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/billing/loading/setEntities' }>
): AccountState => {
  return {
    ...state,
    billing: {
      ...state.billing,
      loading: {
        ...state.billing.loading,
        entities: cmd.payload.isLoading
      }
    }
  };
};

const handleBillingLoadingSetSubmitting = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/billing/loading/setSubmitting' }>
): AccountState => {
  return {
    ...state,
    billing: {
      ...state.billing,
      loading: {
        ...state.billing.loading,
        submitting: cmd.payload.isLoading
      }
    }
  };
};

// =============================================================================
// USERS DATA HANDLERS
// =============================================================================

const handleUsersDataSetAll = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/users/data/setAll' }>
): AccountState => {
  return {
    ...state,
    users: {
      ...state.users,
      list: cmd.payload.users
    }
  };
};

const handleUsersDataSetMembershipRoles = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/users/data/setMembershipRoles' }>
): AccountState => {
  return {
    ...state,
    users: {
      ...state.users,
      membershipRoles: cmd.payload.membershipRoles
    }
  };
};

// =============================================================================
// USERS LIST HANDLERS
// =============================================================================

const handleUsersListSetSearch = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/users/list/setSearch' }>
): AccountState => {
  return {
    ...state,
    users: {
      ...state.users,
      searchQuery: cmd.payload.query,
      currentPage: 1 // Reset to page 1 when search changes
    }
  };
};

const handleUsersListSetPage = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/users/list/setPage' }>
): AccountState => {
  return {
    ...state,
    users: {
      ...state.users,
      currentPage: cmd.payload.page
    }
  };
};

// =============================================================================
// USERS FORM HANDLERS
// =============================================================================

const handleUsersFormOpenAdd = (
  state: AccountState,
  _cmd: Extract<AccountCommand, { type: 'account/users/form/openAdd' }>
): AccountState => {
  return {
    ...state,
    users: {
      ...state.users,
      formMode: { type: 'add' },
      formData: { ...emptyUserFormData },
      formErrors: {}
    }
  };
};

const handleUsersFormOpenEdit = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/users/form/openEdit' }>
): AccountState => {
  const user = selectors.getUserById( state, cmd.payload.userId );
  if ( !user ) {
    console.warn( 'User not found for edit:', cmd.payload.userId );
    return state;
  }

  return {
    ...state,
    users: {
      ...state.users,
      formMode: { type: 'edit', entityId: cmd.payload.userId },
      formData: {
        email: user.email || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        roles: state.users.membershipRoles[user.id] ?? []
      },
      formErrors: {}
    }
  };
};

const handleUsersFormClose = (
  state: AccountState,
  _cmd: Extract<AccountCommand, { type: 'account/users/form/close' }>
): AccountState => {
  return {
    ...state,
    users: {
      ...state.users,
      formMode: { type: 'closed' },
      formData: { ...emptyUserFormData },
      formErrors: {}
    }
  };
};

const handleUsersFormUpdateField = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/users/form/updateField' }>
): AccountState => {
  const newFormData = {
    ...state.users.formData,
    [cmd.payload.field]: cmd.payload.value
  };

  // Clear error for this field
  const newErrors = { ...state.users.formErrors };
  delete newErrors[cmd.payload.field];

  return {
    ...state,
    users: {
      ...state.users,
      formData: newFormData,
      formErrors: newErrors
    }
  };
};

const handleUsersFormSetErrors = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/users/form/setErrors' }>
): AccountState => {
  return {
    ...state,
    users: {
      ...state.users,
      formErrors: cmd.payload.errors
    }
  };
};

const handleUsersFormSubmit = (
  state: AccountState,
  _cmd: Extract<AccountCommand, { type: 'account/users/form/submit' }>
): AccountState => {
  // Mark as submitting (policy will handle backend call)
  return {
    ...state,
    users: {
      ...state.users,
      loading: {
        ...state.users.loading,
        submitting: true
      }
    }
  };
};

// =============================================================================
// USERS LOADING HANDLERS
// =============================================================================

const handleUsersLoadingSetFetching = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/users/loading/setFetching' }>
): AccountState => {
  return {
    ...state,
    users: {
      ...state.users,
      loading: {
        ...state.users.loading,
        fetching: cmd.payload.isLoading
      }
    }
  };
};

const handleUsersLoadingSetSubmitting = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/users/loading/setSubmitting' }>
): AccountState => {
  return {
    ...state,
    users: {
      ...state.users,
      loading: {
        ...state.users.loading,
        submitting: cmd.payload.isLoading
      }
    }
  };
};

// =============================================================================
// RULES DATA HANDLERS
// =============================================================================

const handleRulesDefinitionsSet = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/rules/definitions/set' }>
): AccountState => {
  return {
    ...state,
    rules: {
      ...state.rules,
      definitions: cmd.payload.definitions
    }
  };
};

const handleRulesAppliedSet = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/rules/applied/set' }>
): AccountState => {
  return {
    ...state,
    rules: {
      ...state.rules,
      appliedRules: cmd.payload.rules
    }
  };
};

const handleRulesAppliedAdd = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/rules/applied/add' }>
): AccountState => {
  return {
    ...state,
    rules: {
      ...state.rules,
      appliedRules: [ ...state.rules.appliedRules, cmd.payload.rule ]
    }
  };
};

const handleRulesAppliedUpdate = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/rules/applied/update' }>
): AccountState => {
  return {
    ...state,
    rules: {
      ...state.rules,
      appliedRules: state.rules.appliedRules.map(( rule ) =>
        rule.id === cmd.payload.ruleId || rule.ruleId === cmd.payload.ruleId
          ? { ...rule, ...cmd.payload.rule }
          : rule )
    }
  };
};

const handleRulesAppliedRemove = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/rules/applied/remove' }>
): AccountState => {
  return {
    ...state,
    rules: {
      ...state.rules,
      appliedRules: state.rules.appliedRules.filter(
        ( rule ) => rule.id !== cmd.payload.ruleId && rule.ruleId !== cmd.payload.ruleId
      )
    }
  };
};

// =============================================================================
// RULES ACTION HANDLERS
// =============================================================================

const handleRulesSubmit = (
  state: AccountState,
  _cmd: Extract<AccountCommand, { type: 'account/rules/submit' }>
): AccountState => {
  // Mark as submitting (policy will handle backend call)
  return {
    ...state,
    rules: {
      ...state.rules,
      loading: {
        ...state.rules.loading,
        submitting: true
      }
    }
  };
};

const handleRulesDelete = (
  state: AccountState,
  _cmd: Extract<AccountCommand, { type: 'account/rules/delete' }>
): AccountState => {
  // Mark as submitting (policy will handle backend call)
  return {
    ...state,
    rules: {
      ...state.rules,
      loading: {
        ...state.rules.loading,
        submitting: true
      }
    }
  };
};

// =============================================================================
// RULES LOADING HANDLERS
// =============================================================================

const handleRulesLoadingSetDefinitions = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/rules/loading/setDefinitions' }>
): AccountState => {
  return {
    ...state,
    rules: {
      ...state.rules,
      loading: {
        ...state.rules.loading,
        definitions: cmd.payload.isLoading
      }
    }
  };
};

const handleRulesLoadingSetAppliedRules = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/rules/loading/setAppliedRules' }>
): AccountState => {
  return {
    ...state,
    rules: {
      ...state.rules,
      loading: {
        ...state.rules.loading,
        appliedRules: cmd.payload.isLoading
      }
    }
  };
};

const handleRulesLoadingSetSubmitting = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/rules/loading/setSubmitting' }>
): AccountState => {
  return {
    ...state,
    rules: {
      ...state.rules,
      loading: {
        ...state.rules.loading,
        submitting: cmd.payload.isLoading
      }
    }
  };
};

const handleRulesFormOpenAdd = (
  state: AccountState,
  _cmd: Extract<AccountCommand, { type: 'account/rules/form/openAdd' }>
): AccountState => {
  return {
    ...state,
    rules: {
      ...state.rules,
      openAddTrigger: state.rules.openAddTrigger + 1
    }
  };
};

// =============================================================================
// SKU CONFIG HANDLERS
// =============================================================================

const handleSkuConfigSetData = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/skuConfig/setData' }>
): AccountState => {
  return {
    ...state,
    skuConfig: {
      ...state.skuConfig,
      categories: cmd.payload.categories,
      overrides: cmd.payload.overrides,
      isLoading: false
    }
  };
};

const handleSkuConfigSetSearch = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/skuConfig/setSearch' }>
): AccountState => {
  return {
    ...state,
    skuConfig: {
      ...state.skuConfig,
      search: cmd.payload.search
    }
  };
};

const handleSkuConfigStartEdit = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/skuConfig/startEdit' }>
): AccountState => {
  return {
    ...state,
    skuConfig: {
      ...state.skuConfig,
      editingCategory: { id: cmd.payload.id, formula: cmd.payload.formula }
    }
  };
};

const handleSkuConfigCancelEdit = (
  state: AccountState,
  _cmd: Extract<AccountCommand, { type: 'account/skuConfig/cancelEdit' }>
): AccountState => {
  return {
    ...state,
    skuConfig: {
      ...state.skuConfig,
      editingCategory: null
    }
  };
};

const handleSkuConfigUpdateFormula = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/skuConfig/updateFormula' }>
): AccountState => {
  if ( !state.skuConfig.editingCategory ) return state;

  return {
    ...state,
    skuConfig: {
      ...state.skuConfig,
      editingCategory: {
        ...state.skuConfig.editingCategory,
        formula: cmd.payload.formula
      }
    }
  };
};

const handleSkuConfigSaveStart = (
  state: AccountState,
  _cmd: Extract<AccountCommand, { type: 'account/skuConfig/save/start' }>
): AccountState => {
  if ( !state.skuConfig.editingCategory ) return state;

  return {
    ...state,
    skuConfig: {
      ...state.skuConfig,
      isSaving: state.skuConfig.editingCategory.id
    }
  };
};

const handleSkuConfigSaveSuccess = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/skuConfig/save/success' }>
): AccountState => {
  return {
    ...state,
    skuConfig: {
      ...state.skuConfig,
      overrides: {
        ...state.skuConfig.overrides,
        [cmd.payload.categoryId]: cmd.payload.formula
      },
      isSaving: null,
      editingCategory: null
    }
  };
};

const handleSkuConfigSaveError = (
  state: AccountState,
  _cmd: Extract<AccountCommand, { type: 'account/skuConfig/save/error' }>
): AccountState => {
  return {
    ...state,
    skuConfig: {
      ...state.skuConfig,
      isSaving: null
    }
  };
};

const handleSkuConfigRemoveStart = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/skuConfig/remove/start' }>
): AccountState => {
  return {
    ...state,
    skuConfig: {
      ...state.skuConfig,
      isSaving: cmd.payload.categoryId
    }
  };
};

const handleSkuConfigRemoveSuccess = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/skuConfig/remove/success' }>
): AccountState => {
  const { [cmd.payload.categoryId]: _removed, ...rest } = state.skuConfig.overrides;
  return {
    ...state,
    skuConfig: {
      ...state.skuConfig,
      overrides: rest,
      isSaving: null
    }
  };
};

const handleSkuConfigRemoveError = (
  state: AccountState,
  _cmd: Extract<AccountCommand, { type: 'account/skuConfig/remove/error' }>
): AccountState => {
  return {
    ...state,
    skuConfig: {
      ...state.skuConfig,
      isSaving: null
    }
  };
};

// =============================================================================
// ORDER STATUSES HANDLERS (WS-2 / TV1-1888)
// =============================================================================

const handleOrderStatusesSetData = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/orderStatuses/setData' }>
): AccountState => {
  return {
    ...state,
    orderStatuses: {
      ...state.orderStatuses,
      data: cmd.payload.statuses,
      isLoading: false,
      error: undefined
    }
  };
};

const handleOrderStatusesStartEdit = (
  state: AccountState,
  _cmd: Extract<AccountCommand, { type: 'account/orderStatuses/startEdit' }>
): AccountState => {
  // Seed the draft from the persisted data (no-op if already editing).
  if ( state.orderStatuses.draft !== null ) return state;
  return {
    ...state,
    orderStatuses: {
      ...state.orderStatuses,
      draft: state.orderStatuses.data.map(( s ) => ({ ...s })),
      error: undefined
    }
  };
};

const handleOrderStatusesUpdateRow = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/orderStatuses/updateRow' }>
): AccountState => {
  if ( state.orderStatuses.draft === null ) return state;
  return {
    ...state,
    orderStatuses: {
      ...state.orderStatuses,
      draft: state.orderStatuses.draft.map(( s ) =>
        s.id === cmd.payload.id ? { ...s, ...cmd.payload.changes } : s )
    }
  };
};

const handleOrderStatusesAddRow = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/orderStatuses/addRow' }>
): AccountState => {
  const draft = state.orderStatuses.draft ?? state.orderStatuses.data.map(( s ) => ({ ...s }));
  return {
    ...state,
    orderStatuses: {
      ...state.orderStatuses,
      draft: [ ...draft, cmd.payload.status ]
    }
  };
};

const handleOrderStatusesRemoveRow = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/orderStatuses/removeRow' }>
): AccountState => {
  if ( state.orderStatuses.draft === null ) return state;
  return {
    ...state,
    orderStatuses: {
      ...state.orderStatuses,
      draft: state.orderStatuses.draft.filter(( s ) => s.id !== cmd.payload.id )
    }
  };
};

const handleOrderStatusesReorder = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/orderStatuses/reorder' }>
): AccountState => {
  if ( state.orderStatuses.draft === null ) return state;
  const byId = new Map( state.orderStatuses.draft.map(( s ) => [ s.id, s ]));
  // Reindex `order` to match the new position; drop ids no longer present.
  const reordered = cmd.payload.orderedIds
    .map(( id ) => byId.get( id ))
    .filter(( s ): s is NonNullable<typeof s> => s !== undefined )
    .map(( s, index ) => ({ ...s, order: index }));
  return {
    ...state,
    orderStatuses: {
      ...state.orderStatuses,
      draft: reordered
    }
  };
};

const handleOrderStatusesSaveStart = (
  state: AccountState,
  _cmd: Extract<AccountCommand, { type: 'account/orderStatuses/save/start' }>
): AccountState => {
  // Mark as saving (policy will issue the backend command from the draft).
  if ( state.orderStatuses.draft === null ) return state;
  return {
    ...state,
    orderStatuses: {
      ...state.orderStatuses,
      isSaving: true,
      error: undefined
    }
  };
};

const handleOrderStatusesSaveSuccess = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/orderStatuses/save/success' }>
): AccountState => {
  return {
    ...state,
    orderStatuses: {
      ...state.orderStatuses,
      data: cmd.payload.statuses,
      draft: null,
      isSaving: false,
      error: undefined
    }
  };
};

const handleOrderStatusesSaveError = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/orderStatuses/save/error' }>
): AccountState => {
  return {
    ...state,
    orderStatuses: {
      ...state.orderStatuses,
      isSaving: false,
      error: cmd.payload.error
    }
  };
};

// =============================================================================
// TOP-LEVEL LOADING HANDLERS
// =============================================================================

const handleLoadingSetAccount = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/loading/setAccount' }>
): AccountState => {
  return {
    ...state,
    loading: {
      ...state.loading,
      account: cmd.payload.isLoading
    }
  };
};

// =============================================================================
// ASSIGNMENT GROUPS HANDLERS (TV1-1878)
// =============================================================================

const handleGroupsDataSetAll = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/groups/data/setAll' }>
): AccountState => ({
  ...state,
  assignmentGroups: { ...state.assignmentGroups, list: cmd.payload.groups }
});

const handleGroupsDataUpsert = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/groups/data/upsert' }>
): AccountState => {
  const { group } = cmd.payload;
  const exists = state.assignmentGroups.list.some(( g ) => g.id === group.id );
  const list = exists
    ? state.assignmentGroups.list.map(( g ) => ( g.id === group.id ? group : g ))
    : [ ...state.assignmentGroups.list, group ];
  return { ...state, assignmentGroups: { ...state.assignmentGroups, list }};
};

const handleGroupsDataRemove = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/groups/data/remove' }>
): AccountState => {
  const { groupId } = cmd.payload;
  return {
    ...state,
    assignmentGroups: {
      ...state.assignmentGroups,
      list: state.assignmentGroups.list.filter(( g ) => g.id !== groupId )
    }
  };
};

const handleGroupsListSetSearch = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/groups/list/setSearch' }>
): AccountState => ({
  ...state,
  assignmentGroups: { ...state.assignmentGroups, searchQuery: cmd.payload.query }
});

const handleGroupsFormOpenAdd = (
  state: AccountState,
  _cmd: Extract<AccountCommand, { type: 'account/groups/form/openAdd' }>
): AccountState => ({
  ...state,
  assignmentGroups: {
    ...state.assignmentGroups,
    formMode: { type: 'add' },
    formData: emptyGroupFormData,
    formErrors: {}
  }
});

const handleGroupsFormOpenRename = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/groups/form/openRename' }>
): AccountState => {
  const group = state.assignmentGroups.list.find(( g ) => g.id === cmd.payload.groupId );
  return {
    ...state,
    assignmentGroups: {
      ...state.assignmentGroups,
      formMode: { type: 'rename', groupId: cmd.payload.groupId },
      // Prefill name AND current members so the form opens pre-populated.
      formData: { name: group?.name ?? '', memberUserIds: [...( group?.memberUserIds ?? [])]},
      formErrors: {}
    }
  };
};

const handleGroupsFormClose = (
  state: AccountState,
  _cmd: Extract<AccountCommand, { type: 'account/groups/form/close' }>
): AccountState => ({
  ...state,
  assignmentGroups: {
    ...state.assignmentGroups,
    formMode: { type: 'closed' },
    formData: emptyGroupFormData,
    formErrors: {}
  }
});

const handleGroupsFormUpdateName = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/groups/form/updateName' }>
): AccountState => ({
  ...state,
  assignmentGroups: {
    ...state.assignmentGroups,
    formData: { ...state.assignmentGroups.formData, name: cmd.payload.name }
  }
});

const handleGroupsFormUpdateMembers = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/groups/form/updateMembers' }>
): AccountState => ({
  ...state,
  assignmentGroups: {
    ...state.assignmentGroups,
    formData: { ...state.assignmentGroups.formData, memberUserIds: cmd.payload.memberUserIds }
  }
});

const handleGroupsFormSubmit = (
  state: AccountState,
  _cmd: Extract<AccountCommand, { type: 'account/groups/form/submit' }>
): AccountState => {
  // Set the pending command for the submit policy to act on. The policy mints
  // the new group id on create, issues the backend command(s) (including the
  // batch member add/remove), and closes the form.
  const { formMode, formData } = state.assignmentGroups;
  if ( formMode.type === 'closed' || !formData.name.trim()) {
    return state;
  }
  const memberUserIds = [...formData.memberUserIds];
  const pendingCommand =
    formMode.type === 'rename'
      ? { kind: 'rename' as const, groupId: formMode.groupId, name: formData.name.trim(), memberUserIds }
      : { kind: 'create' as const, name: formData.name.trim(), memberUserIds };
  return {
    ...state,
    assignmentGroups: {
      ...state.assignmentGroups,
      pendingCommand,
      loading: { ...state.assignmentGroups.loading, submitting: true }
    }
  };
};

const handleGroupsDelete = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/groups/delete' }>
): AccountState => {
  // Mark a pending delete; the submit policy issues backend DeleteAssignmentGroup
  // and dispatches the optimistic data/remove.
  return {
    ...state,
    assignmentGroups: {
      ...state.assignmentGroups,
      pendingCommand: { kind: 'delete', groupId: cmd.payload.groupId }
    }
  };
};

const handleGroupsPendingClear = (
  state: AccountState,
  _cmd: Extract<AccountCommand, { type: 'account/groups/pending/clear' }>
): AccountState => ({
  ...state,
  assignmentGroups: { ...state.assignmentGroups, pendingCommand: null }
});

const handleGroupsLoadingSetFetching = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/groups/loading/setFetching' }>
): AccountState => ({
  ...state,
  assignmentGroups: {
    ...state.assignmentGroups,
    loading: { ...state.assignmentGroups.loading, fetching: cmd.payload.isLoading }
  }
});

const handleGroupsLoadingSetSubmitting = (
  state: AccountState,
  cmd: Extract<AccountCommand, { type: 'account/groups/loading/setSubmitting' }>
): AccountState => ({
  ...state,
  assignmentGroups: {
    ...state.assignmentGroups,
    loading: { ...state.assignmentGroups.loading, submitting: cmd.payload.isLoading }
  }
});

// =============================================================================
// HANDLER MAP
// =============================================================================

export const handlers: CommandHandlerMap<AccountState, AccountCommand> = {
  // Tab
  'account/tab/set': handleTabSet,

  // Account Data
  'account/data/set': handleDataSetAccount,
  'account/data/clear': handleDataClearAccount,

  // Account Info
  'account/info/startEdit': handleInfoStartEdit,
  'account/info/updateTemp': handleInfoUpdateTemp,
  'account/info/save': handleInfoSave,
  'account/info/cancel': handleInfoCancel,
  'account/info/loading/setSaving': handleInfoSetSaving,

  // Billing Data
  'account/billing/data/upsert': handleBillingDataUpsert,
  'account/billing/data/remove': handleBillingDataRemove,
  'account/billing/data/setAll': handleBillingDataSetAll,
  'account/billing/data/setInvoicingIntegrations': handleBillingDataSetInvoicingIntegrations,

  // Billing List
  'account/billing/list/setSearch': handleBillingListSetSearch,
  'account/billing/list/setPage': handleBillingListSetPage,

  // Billing Form
  'account/billing/form/openAdd': handleBillingFormOpenAdd,
  'account/billing/form/openEdit': handleBillingFormOpenEdit,
  'account/billing/form/close': handleBillingFormClose,
  'account/billing/form/updateField': handleBillingFormUpdateField,
  'account/billing/form/setErrors': handleBillingFormSetErrors,
  'account/billing/form/setPointsOfSale': handleBillingFormSetPointsOfSale,
  'account/billing/form/removePointOfSale': handleBillingFormRemovePointOfSale,
  'account/billing/arcaPointsOfSale/loading': handleArcaPointsOfSaleLoading,
  'account/billing/arcaPointsOfSale/loaded': handleArcaPointsOfSaleLoaded,
  'account/billing/arcaPointsOfSale/failed': handleArcaPointsOfSaleFailed,
  'account/billing/form/submit': handleBillingFormSubmit,

  // Billing Loading
  'account/billing/loading/setEntities': handleBillingLoadingSetEntities,
  'account/billing/loading/setSubmitting': handleBillingLoadingSetSubmitting,

  // Users Data
  'account/users/data/setAll': handleUsersDataSetAll,
  'account/users/data/setMembershipRoles': handleUsersDataSetMembershipRoles,

  // Users List
  'account/users/list/setSearch': handleUsersListSetSearch,
  'account/users/list/setPage': handleUsersListSetPage,

  // Users Form
  'account/users/form/openAdd': handleUsersFormOpenAdd,
  'account/users/form/openEdit': handleUsersFormOpenEdit,
  'account/users/form/close': handleUsersFormClose,
  'account/users/form/updateField': handleUsersFormUpdateField,
  'account/users/form/setErrors': handleUsersFormSetErrors,
  'account/users/form/submit': handleUsersFormSubmit,

  // Users Loading
  'account/users/loading/setFetching': handleUsersLoadingSetFetching,
  'account/users/loading/setSubmitting': handleUsersLoadingSetSubmitting,

  // Assignment groups (TV1-1878)
  'account/groups/data/setAll': handleGroupsDataSetAll,
  'account/groups/data/upsert': handleGroupsDataUpsert,
  'account/groups/data/remove': handleGroupsDataRemove,
  'account/groups/list/setSearch': handleGroupsListSetSearch,
  'account/groups/form/openAdd': handleGroupsFormOpenAdd,
  'account/groups/form/openRename': handleGroupsFormOpenRename,
  'account/groups/form/close': handleGroupsFormClose,
  'account/groups/form/updateName': handleGroupsFormUpdateName,
  'account/groups/form/updateMembers': handleGroupsFormUpdateMembers,
  'account/groups/form/submit': handleGroupsFormSubmit,
  'account/groups/delete': handleGroupsDelete,
  'account/groups/pending/clear': handleGroupsPendingClear,
  'account/groups/loading/setFetching': handleGroupsLoadingSetFetching,
  'account/groups/loading/setSubmitting': handleGroupsLoadingSetSubmitting,

  // Rules Data
  'account/rules/definitions/set': handleRulesDefinitionsSet,
  'account/rules/applied/set': handleRulesAppliedSet,
  'account/rules/applied/add': handleRulesAppliedAdd,
  'account/rules/applied/update': handleRulesAppliedUpdate,
  'account/rules/applied/remove': handleRulesAppliedRemove,

  // Rules Actions
  'account/rules/form/openAdd': handleRulesFormOpenAdd,
  'account/rules/submit': handleRulesSubmit,
  'account/rules/delete': handleRulesDelete,

  // Rules Loading
  'account/rules/loading/setDefinitions': handleRulesLoadingSetDefinitions,
  'account/rules/loading/setAppliedRules': handleRulesLoadingSetAppliedRules,
  'account/rules/loading/setSubmitting': handleRulesLoadingSetSubmitting,

  // SKU Config
  'account/skuConfig/setData': handleSkuConfigSetData,
  'account/skuConfig/setSearch': handleSkuConfigSetSearch,
  'account/skuConfig/startEdit': handleSkuConfigStartEdit,
  'account/skuConfig/cancelEdit': handleSkuConfigCancelEdit,
  'account/skuConfig/updateFormula': handleSkuConfigUpdateFormula,
  'account/skuConfig/save/start': handleSkuConfigSaveStart,
  'account/skuConfig/save/success': handleSkuConfigSaveSuccess,
  'account/skuConfig/save/error': handleSkuConfigSaveError,
  'account/skuConfig/remove/start': handleSkuConfigRemoveStart,
  'account/skuConfig/remove/success': handleSkuConfigRemoveSuccess,
  'account/skuConfig/remove/error': handleSkuConfigRemoveError,

  // Order Statuses (WS-2 / TV1-1888)
  'account/orderStatuses/setData': handleOrderStatusesSetData,
  'account/orderStatuses/startEdit': handleOrderStatusesStartEdit,
  'account/orderStatuses/updateRow': handleOrderStatusesUpdateRow,
  'account/orderStatuses/addRow': handleOrderStatusesAddRow,
  'account/orderStatuses/removeRow': handleOrderStatusesRemoveRow,
  'account/orderStatuses/reorder': handleOrderStatusesReorder,
  'account/orderStatuses/save/start': handleOrderStatusesSaveStart,
  'account/orderStatuses/save/success': handleOrderStatusesSaveSuccess,
  'account/orderStatuses/save/error': handleOrderStatusesSaveError,

  // Top-level Loading
  'account/loading/setAccount': handleLoadingSetAccount
};
